import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Mounts below-the-fold content once it approaches the viewport (or shortly
 * after load as a fallback, so crawlers and non-scrolling visitors still get
 * the full page). Keeps the initial React render small, which lowers blocking
 * time and speeds up the hero paint.
 */
const DeferredSection = ({
  children,
  minHeight = 480,
  fallbackDelayMs = 2500,
}: {
  children: ReactNode;
  minHeight?: number;
  fallbackDelayMs?: number;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const node = ref.current;
    let timer: number | undefined;

    const observer =
      node && typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting)) setShow(true);
            },
            { rootMargin: "700px 0px" },
          )
        : null;
    if (observer && node) observer.observe(node);

    const schedule = () => {
      timer = window.setTimeout(() => setShow(true), fallbackDelayMs);
    };
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      observer?.disconnect();
      if (timer) window.clearTimeout(timer);
    };
  }, [show, fallbackDelayMs]);

  return (
    <div ref={ref} style={show ? undefined : { minHeight }}>
      {show ? <Suspense fallback={null}>{children}</Suspense> : null}
    </div>
  );
};

export default DeferredSection;
